import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output } from '@angular/core';
import { FormControl } from '@angular/forms';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { debounceTime, distinctUntilChanged, finalize, forkJoin, Observable, of, Subject, takeUntil } from 'rxjs';
import { ICoachTask } from '@gray/domain/models/coach/coach-task.model';
import { CoachStudentContentService } from '../../../core/services/coach-student-content.service';
import { CoachTaskService } from '../../../core/services/coach-task.service';
import { CoachPeriodService } from '../../../core/services/coach-period.service';
import { IContentItem, liveStreamToContentItem, videoToContentItem } from '../../../core/models/content-item.model';
import { ContentPreviewModalComponent } from './content-preview-modal/content-preview-modal.component';
import { AssignContentModalComponent } from './assign-content-modal/assign-content-modal.component';

const PAGE_SIZE = 8;
/** Assignments per student stay in the dozens, so one page covers both the list and the card state. */
const ASSIGNED_SIZE = 100;

type TContentFilter = 'all' | 'video' | 'live-stream';

/**
 * The content the student can open, and what of it the coach has told them to watch.
 *
 * The library is the student's own — the API answers through them, so level filters and trial
 * locks are already applied — and assigning is an ordinary task that happens to name a video or a
 * stream. That is why the top of the tab is a task list: those are the same records the task tab
 * shows, narrowed to the ones carrying content.
 */
@Component({
  selector: 'coach-videos-tab',
  templateUrl: './videos-tab.component.html',
  styleUrl: './videos-tab.component.scss',
})
export class VideosTabComponent implements OnInit, OnChanges, OnDestroy {
  @Input() studentId!: number;
  @Input() studentName?: string | null;

  /** Assigning writes a task, so the tab bar's task counter is out of date afterwards. */
  @Output() tasksChanged = new EventEmitter<void>();

  items: IContentItem[] = [];
  assignedTasks: ICoachTask[] = [];

  filter: TContentFilter = 'all';
  searchControl = new FormControl<string>('');

  page = 1;
  hasMore = false;

  isLoading = false;
  assignedIsLoading = false;

  destroyed$ = new Subject();

  constructor(
    private contentService: CoachStudentContentService,
    private taskService: CoachTaskService,
    private periodService: CoachPeriodService,
    private translate: TranslateService,
    private toastrService: ToastrService,
    private modalService: NgbModal,
  ) {
  }

  ngOnInit(): void {
    this.searchControl.valueChanges
      .pipe(takeUntil(this.destroyed$), debounceTime(400), distinctUntilChanged())
      .subscribe(() => this.reload());
  }

  ngOnChanges(): void {
    if (this.studentId) {
      this.items = [];
      this.assignedTasks = [];
      this.reload();
    }
  }

  private periodId(): number | null {
    return this.periodService.selectedId;
  }

  reload() {
    this.page = 1;
    this.getItems();
    this.getAssigned();
  }

  /**
   * Videos and live streams live behind two endpoints but read as one library, so "all" asks both
   * and merges them newest first. Either side still having a page is enough to keep loading.
   */
  getItems(append = false) {
    const queryString = this.buildQuery();

    const videos$: Observable<any> = this.filter === 'live-stream'
      ? of(null)
      : this.contentService.getVideos(this.studentId, this.periodId(), queryString);
    const streams$: Observable<any> = this.filter === 'video'
      ? of(null)
      : this.contentService.getLiveStreams(this.studentId, this.periodId(), queryString);

    this.isLoading = true;
    forkJoin({videos: videos$, streams: streams$})
      .pipe(takeUntil(this.destroyed$), finalize(() => this.isLoading = false))
      .subscribe(r => {
        const videos = (r.videos?.data?.result ?? []).map(videoToContentItem);
        const streams = (r.streams?.data?.result ?? []).map(liveStreamToContentItem);

        const loaded = [...videos, ...streams]
          .sort((a, b) => new Date(b.date ?? 0).getTime() - new Date(a.date ?? 0).getTime());

        this.items = append ? [...this.items, ...loaded] : loaded;
        this.hasMore = this.pageHasMore(r.videos) || this.pageHasMore(r.streams);
      });
  }

  private pageHasMore(response: any): boolean {
    const meta = response?.data?.meta;
    return !!meta && meta.page < meta.lastPage;
  }

  private buildQuery(): string {
    const parts = [`page=${this.page}`, `size=${PAGE_SIZE}`];

    const search = this.searchControl.value?.trim();
    if (search) {
      parts.push(`search=${encodeURIComponent(search)}`);
    }

    return parts.join('&');
  }

  loadMore() {
    if (this.isLoading || !this.hasMore) {
      return;
    }

    this.page += 1;
    this.getItems(true);
  }

  setFilter(filter: TContentFilter) {
    if (this.filter === filter) {
      return;
    }

    this.filter = filter;
    this.page = 1;
    this.items = [];
    this.getItems();
  }

  /** The tasks that point at content: the list at the top, and what marks a card as assigned. */
  getAssigned(silent = false) {
    this.assignedIsLoading = !silent;
    this.taskService.getTasks(this.studentId, `?page=1&size=${ASSIGNED_SIZE}&filter[hasContent]=true`)
      .pipe(takeUntil(this.destroyed$), finalize(() => this.assignedIsLoading = false))
      .subscribe(r => this.assignedTasks = (r.data?.result ?? []) as ICoachTask[]);
  }

  isAssigned(item: IContentItem): boolean {
    return this.assignedTasks.some(task => item.type === 'video'
      ? task.videoId === item.id
      : task.liveStreamId === item.id);
  }

  preview(item: IContentItem) {
    const modalRef = this.modalService.open(ContentPreviewModalComponent, {
      size: 'lg',
      centered: true,
      animation: true,
      scrollable: true,
    });
    modalRef.componentInstance.studentId = this.studentId;
    modalRef.componentInstance.periodId = this.periodId();
    modalRef.componentInstance.item = item;
    modalRef.componentInstance.isAssigned = this.isAssigned(item);

    modalRef.componentInstance.assignRequested
      .pipe(takeUntil(this.destroyed$))
      .subscribe((requested: IContentItem) => this.assign(requested));
  }

  assign(item: IContentItem) {
    const modalRef = this.modalService.open(AssignContentModalComponent, {
      size: 'md',
      centered: true,
      animation: true,
    });
    modalRef.componentInstance.studentId = this.studentId;
    modalRef.componentInstance.periodId = this.periodId();
    modalRef.componentInstance.item = item;

    modalRef.closed.pipe(takeUntil(this.destroyed$)).subscribe(() => {
      this.getAssigned(true);
      this.tasksChanged.emit();
    });
  }

  /** Taking an assignment back removes the task itself; the student would otherwise still see it. */
  async confirmRemove(task: ICoachTask) {
    const Swal = (await import('sweetalert2')).default;
    const result = await Swal.fire({
      text: this.translate.instant('students.videos.remove_confirm'),
      showCancelButton: true,
      confirmButtonText: this.translate.instant('buttons.yes'),
      cancelButtonText: this.translate.instant('buttons.cancel_l'),
      heightAuto: false,
    });

    if (result.isConfirmed) {
      this.remove(task);
    }
  }

  private remove(task: ICoachTask) {
    // Optimistic: the row is gone before the request answers, and comes back if it fails.
    const previous = this.assignedTasks;
    this.assignedTasks = this.assignedTasks.filter(item => item.id !== task.id);

    this.taskService.deleteTask(task.id)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: () => {
          this.toastrService.success(this.translate.instant('students.videos.removed_toast'));
          this.tasksChanged.emit();
        },
        error: () => {
          this.assignedTasks = previous;
          this.toastrService.error(this.translate.instant('students.videos.remove_error'));
        },
      });
  }

  taskContentType(task: ICoachTask): 'video' | 'live-stream' {
    return task.liveStreamId ? 'live-stream' : 'video';
  }

  taskContentTitle(task: ICoachTask): string {
    return task.video?.title ?? task.liveStream?.title ?? task.content;
  }

  taskDuration(task: ICoachTask): number | null {
    return task.video?.duration ?? task.liveStream?.video?.duration ?? null;
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }
}
